import { campaign } from '../../constants';
import { MessageType } from '../../services/track.service';
import type { CampaignConfig, CampaignConfigInput } from './types';

// Generic over the input so each entry's `id` stays a literal type — that is
// what makes CampaignId a union of known ids instead of `string`.
const withDefaults = <T extends CampaignConfigInput>(
	input: T
): CampaignConfig & { id: T['id'] } => ({
	cooldownMs: 0,
	messageType: MessageType.DIALOG,
	dismissOnDisplay: false,
	...input,
});

export const campaigns = {
	searchFeedback: withDefaults({
		id: 'search-feedback',
		maxDisplays: 1,
		priority: 100,
		trackingCampaign: campaign.searchRelevanceFeedback,
		dismissOnDisplay: true,
	}),
	schoolAdminHint: withDefaults({
		id: 'school-admin-hint',
		messageType: MessageType.HINT,
		maxDisplays: 1,
		cooldownMs: 0,
		dismissOnDisplay: false,
		priority: 100,
		trackingCampaign: campaign.schoolAdminHint,
		walkthroughStep: 1,
	}),
	// Shown every session until dismissed; the session mutex is the only cap.
	annotationTabsHint: withDefaults({
		id: 'correction-annotation-tabs-hint',
		messageType: MessageType.HINT,
		maxDisplays: Number.POSITIVE_INFINITY,
		priority: 100,
		trackingCampaign: campaign.correctionToolOnboarding,
		walkthroughStep: 1,
	}),
} satisfies Record<string, CampaignConfig>;

export type CampaignId = (typeof campaigns)[keyof typeof campaigns]['id'];

export const getCampaignConfig = (id: CampaignId): CampaignConfig => {
	const config = Object.values(campaigns).find((entry) => entry.id === id);
	if (!config) {
		throw new Error(`Unknown campaign id: ${id}`);
	}
	return config;
};
